import { Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { locale, tx } from '../../i18n';
import { api } from '../../lib/api';
import type { StreetViewStatus } from '../../types/api';

function captureMonth(value: string | null): string | null {
  if (!value) {
    return null;
  }
  const [year, month] = value.split('-').map(Number);
  if (!year || !month) {
    return null;
  }
  return new Intl.DateTimeFormat(locale(), { month: 'short', year: 'numeric' }).format(new Date(year, month - 1, 1));
}

/**
 * The Street View photo across the top of the report.
 *
 * <p>The status call is free; the image is only requested when Google holds a panorama and today's
 * allowance is not used up. If anything fails the photo simply is not there - it is a nicety,
 * never part of the assessment.
 *
 * <p>What it is not is a photograph of this home. Street View is panned to the street outside the
 * address's coordinates, and the building in the middle of the frame may be next door or across
 * the road. A tenant who takes it for a listing photo has been misled by us, so the disclosure is
 * on the image itself, in a badge that cannot be scrolled past, rather than in a caption under it
 * that reads like a credit line.
 */
export default function StreetViewPhoto({ analysisId }: { analysisId: string }) {
  const [status, setStatus] = useState<StreetViewStatus | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .streetView(analysisId, controller.signal)
      .then(setStatus)
      .catch(() => setStatus(null));
    return () => controller.abort();
  }, [analysisId]);

  if (!status?.available || failed) {
    return null;
  }

  const month = captureMonth(status.imageDate);

  return (
    <figure className={loaded ? 'street-view street-view--loaded' : 'street-view'}>
      <img
        src={api.streetViewImageUrl(analysisId, status.imagePath)}
        alt={tx(
          'Street View of the street outside this address. The building in the picture is not verified as this home.',
          'Bu adresin önündeki sokağın Street View görüntüsü. Resimdeki binanın bu konut olduğu doğrulanmamıştır.',
          'Widok ulicy przed tym adresem. Budynek na zdjęciu nie został potwierdzony jako to mieszkanie.',
          'Vista da rua em frente a esta morada. O edifício da imagem não está confirmado como sendo esta casa.',
          'Vista de la calle frente a esta dirección. No está confirmado que el edificio de la imagen sea esta vivienda.',
        )}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
      />
      <p className="street-view__disclosure">
        <Info size={16} aria-hidden="true" />
        <span>
          {tx(
            'Street view near this address — building not verified',
            'Bu adresin yakınındaki sokak görünümü — bina doğrulanmadı',
            'Widok ulicy w pobliżu tego adresu — budynek niezweryfikowany',
            'Vista da rua perto desta morada — edifício não verificado',
            'Vista de la calle cerca de esta dirección — edificio sin verificar',
          )}
        </span>
      </p>
      <figcaption className="street-view__caption">
        <span>
          {tx(
            'Google pans this photo to the street at the address coordinates, so the building in the middle of the frame may be a neighbour. It is not a photo of the home and is not part of the score.',
            'Google bu fotoğrafı adresin koordinatlarındaki sokağa çevirir; karenin ortasındaki bina komşu olabilir. Konutun fotoğrafı değildir ve puana dahil değildir.',
            'Google kieruje to zdjęcie na ulicę pod współrzędnymi adresu, więc budynek pośrodku kadru może być sąsiedni. To nie jest zdjęcie mieszkania i nie wlicza się do oceny.',
            'A Google aponta esta fotografia à rua nas coordenadas da morada, pelo que o edifício ao centro pode ser o do lado. Não é uma foto da casa e não entra na pontuação.',
            'Google orienta esta foto a la calle en las coordenadas de la dirección, así que el edificio del centro puede ser el de al lado. No es una foto de la vivienda y no cuenta para la puntuación.',
          )}
          {month
            ? ' '
              + tx(
                  `Taken ${month}.`,
                  `Çekim tarihi ${month}.`,
                  `Zdjęcie z ${month}.`,
                  `Tirada em ${month}.`,
                  `Tomada en ${month}.`,
                )
            : ''}
        </span>
        <span className="street-view__credit">{status.attribution}</span>
      </figcaption>
    </figure>
  );
}
